import { useEffect, useRef, useState, useCallback } from "react";
import { Home } from "lucide-react";
import { SpaceEngine } from "./gameEngine";
import {
  createCombat, attackUnit, healUnit, aliveOf, teamDead, lowestHp,
} from "./combatEngine";
import { Cooldowns, FLOAT_COLORS } from "./hudController";
import { sfx } from "./audio";
import { SpaceMuteBtn } from "./SpaceNav";
import { spaceApi } from "./spaceApi";
import hudSkins from "./hudSkins";

const BGM_URL = "/assets/vault/audio/command_deck_theme.mp3";
import {
  loadState, saveState, clearTile, engageTile, corridorComplete,
  TileState, CORRIDOR_TILES,
} from "./stateManager";

// Corridor Energy Surge — accelerated Ultimate-meter gain so slot-4 finishers
// charge fast enough to be seen/recorded during the short Stage-1 fights.
const AUTO_ENERGY = 30;   // per landed hero auto-attack (surge tier, was 10)
const SKILL_ENERGY = 50;  // per basic (non-ultimate) skill cast (surge tier, was 20)

// Interactive Stage-1 outdoor trek: click-to-move across the 5-square corridor,
// real-time skill combat, Nancy rescue, and the Gatehouse Alpha boss → base map.
export default function StageOneTrek({ onComplete, onHome }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const combatRef = useRef(null);
  const cdRef = useRef(new Cooldowns());
  const enemyTimerRef = useRef(null);
  const stateRef = useRef(loadState());
  const skinColorsRef = useRef({});
  const squadRef = useRef([]);
  const backdropRef = useRef(null);
  const enemyCardsRef = useRef({});
  const bossBackdropRef = useRef(null);
  const [squad, setSquad] = useState([]);

  const applyAssets = useCallback(() => {
    const eng = engineRef.current;
    if (!eng) return;
    const BK = process.env.REACT_APP_BACKEND_URL;
    eng.setSquad((squadRef.current || []).map((h) => ({
      cardId: h.card_id, tier: h.tier, color: h.color,
      url: h.image_path ? (h.image_path.startsWith("http") ? h.image_path : BK + h.image_path) : null,
    })));
    if (backdropRef.current) eng.setBackdrop(backdropRef.current.startsWith("http") ? backdropRef.current : BK + backdropRef.current);
  }, []);

  useEffect(() => {
    spaceApi.skinsGet().then((d) => {
      const map = {};
      Object.entries(d.equipped || {}).forEach(([hero, id]) => {
        const c = (d.catalog || []).find((x) => x.id === id);
        if (c) map[hero] = c.color;
      });
      skinColorsRef.current = map;
    }).catch(() => {});
    // Load the deployed squad's profile combat data (stats + active skill trees).
    spaceApi.heroesCombat().then((d) => { squadRef.current = d.squad || []; setSquad(d.squad || []); applyAssets(); }).catch(() => {});
    hudSkins.load();  // preload phase HUD skin frames
    // Dynamic asset-binding: trek backdrop + Zornelex enemy cards from the Stock
    // Library (resolved server-side, so it works in preview AND production).
    spaceApi.gameAssets().then((a) => {
      backdropRef.current = a.trek_backdrop || null;
      bossBackdropRef.current = a.boss_backdrop || null;
      enemyCardsRef.current = a.enemy_cards || {};
      applyAssets();
    }).catch(() => {});
  }, [applyAssets]);

  const [scene, setScene] = useState("trek"); // trek | combat | victory | defeat | gate
  const [toast, setToast] = useState(null);
  const [square, setSquare] = useState(0);
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);

  const pushTrek = useCallback(() => {
    const st = stateRef.current;
    const cleared = st.corridor.map((v, i) => (v === TileState.CLEARED ? i : -1)).filter((i) => i >= 0);
    engineRef.current?.setTrek({
      corridor: st.corridor,
      avatarTile: cleared.length ? Math.max(...cleared) : 0,
      nancy: !!st.heroes.nancy,
    });
    engineRef.current?.setMode("trek");
  }, []);

  const flashToast = (msg, ms = 2200) => {
    setToast(msg);
    window.clearTimeout(flashToast._t);
    flashToast._t = window.setTimeout(() => setToast(null), ms);
  };

  // Fire-and-forget push of campaign progress to the authoritative server save.
  const syncUp = () => {
    const st = stateRef.current;
    spaceApi.syncProgress({ corridor: st.corridor, heroes: st.heroes }).catch(() => {});
  };

  // ── Square arrival handler ────────────────────────────────────────────────
  const onTileArrive = useCallback((index) => {
    const st = stateRef.current;
    if (st.corridor[index] !== TileState.UNCLEARED) return;
    setSquare(index);
    if (index === 0) {
      // Wreckage Path — auto-collect supply cache, no combat.
      stateRef.current = saveState(clearTile(st, 0));
      flashToast("Supply cache: +10 Ammo · +50 Scrap");
      pushTrek();
      syncUp();
      return;
    }
    // Combat squares — require the squad before flagging engaged (avoids a
    // soft-lock where the tile flips to ENGAGED but combat never starts).
    if (!squadRef.current.length) { flashToast("Squad still syncing — try again"); return; }
    stateRef.current = saveState(engageTile(st, index));
    startCombat(index);
  }, [pushTrek]);

  // ── Combat lifecycle ──────────────────────────────────────────────────────
  const startCombat = (index) => {
    if (!squadRef.current.length) { flashToast("Squad still syncing — try again"); return; }
    cdRef.current.reset();
    combatRef.current = createCombat(squadRef.current, index, stateRef.current.commandCentreLevel, skinColorsRef.current);
    // Bind fresh Zornelex enemy-card art from the Stock Library (prod-safe, keyed
    // by species token) over the encounter defaults.
    const CARD_TOKEN = { skitterer: "skitter", raider_a: "vanguard", raider_b: "vanguard", elite: "sprawler", goliath: "alpha" };
    const cards = enemyCardsRef.current || {};
    for (const foe of combatRef.current.enemies) {
      const url = cards[CARD_TOKEN[foe.id] || foe.id];
      if (url) foe.card = url;
    }
    // Boss fights get the dedicated space-battle backdrop when catalogued.
    const bd = combatRef.current.boss && bossBackdropRef.current ? bossBackdropRef.current : backdropRef.current;
    if (bd) { const BK = process.env.REACT_APP_BACKEND_URL; engineRef.current.setBackdrop(bd.startsWith("http") ? bd : BK + bd); }
    engineRef.current.setCombat(combatRef.current);
    engineRef.current.setMode("combat");
    // Boss-spawn cinematic — meters are force-charged to 100% in createCombat, so
    // flash the "SQUAD ULTIMATES READY!" banner the instant the Goliath appears.
    if (combatRef.current.boss) engineRef.current.ultBanner();
    setScene("combat");
    rerender();
    startEnemyLoop();
  };

  const startEnemyLoop = () => {
    stopEnemyLoop();
    enemyTimerRef.current = window.setInterval(() => {
      const c = combatRef.current;
      if (!c || sceneRef.current !== "combat") return;
      const now = performance.now();
      // Restore expired armour-shred (Valkyrie Aegis Cleave).
      for (const foe of c.enemies) {
        if (foe.defShredUntil && now >= foe.defShredUntil) { foe.def += foe.defShredRestore || 0; foe.defShredUntil = 0; foe.defShredRestore = 0; }
      }
      const odActive = now < (c.overdriveUntil || 0);   // Roxy Overdrive Cascade window
      const cadenceMul = odActive ? (1 / 1.5) : 1;       // +50% auto-attack frequency
      for (const e of aliveOf(c.enemies)) {
        if (now < (e.stunnedUntil || 0)) continue;
        if (now - e.lastAtk < e.atkCd) continue;
        e.lastAtk = now;
        const heroesAlive = aliveOf(c.heroes);
        if (!heroesAlive.length) break;
        const taunter = heroesAlive.find((h) => h.taunt);
        const targets = e.sweep ? heroesAlive : [taunter || lowestHp(c.heroes)];
        for (const tgt of targets) {
          if (!tgt) continue;
          const { dmg, absorbed, crit } = attackUnit(e, tgt, 1);
          if (dmg > 0) { engineRef.current.spawnProjectile(e, tgt, "#ff5a6a", crit); engineRef.current.spawnFloatAtUnit(tgt, `-${dmg}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit); }
          else if (absorbed > 0) engineRef.current.spawnFloatAtUnit(tgt, "BLOCK", FLOAT_COLORS.info);
        }
      }
      if (teamDead(c.heroes)) return endCombat(false);

      // Hero passive auto-attacks — each deployed hero fires on its own cadence,
      // charging the Tier-4 Ultimate meter (+10 energy per landed auto-attack).
      const eng = engineRef.current;
      for (const h of aliveOf(c.heroes)) {
        if (now - (h.lastAuto || 0) < (h.autoCd || 1500) * cadenceMul) continue;
        const target = lowestHp(c.enemies);
        if (!target) break;
        h.lastAuto = now;
        const { dmg, crit } = attackUnit(h, target, 1);
        eng.spawnProjectile(h, target, crit ? FLOAT_COLORS.crit : (odActive ? "#ff36c8" : (h.color || "#4aa3ff")), crit);
        eng.spawnFloatAtUnit(target, `-${dmg}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit);
        if (odActive && dmg > 0) { const steal = Math.round(dmg * (c.overdriveLifesteal || 0.30)); if (steal > 0) { healUnit(h, steal); eng.spawnFloatAtUnit(h, `+${steal}`, FLOAT_COLORS.heal); } }
        h.energy = Math.min(h.maxEnergy, h.energy + AUTO_ENERGY);
      }
      if (aliveOf(c.enemies).length === 0) return endCombat(true);
      rerender();
    }, 240);
  };
  const stopEnemyLoop = () => { if (enemyTimerRef.current) window.clearInterval(enemyTimerRef.current); enemyTimerRef.current = null; };

  // Generic armour debuff — lowers a foe's defense so ALL follow-up damage lands
  // harder (the enemy loop auto-restores it on expiry). Shared by every skill that
  // carries def_shred/shred_ms (Riany's Corrosive Strike, Valkyrie's Aegis Cleave…).
  const applyArmourShred = (foe, sk, now, eng) => {
    const pct = sk.def_shred || 0;
    if (pct <= 0 || !foe) return;
    if (!foe.defShredUntil || now >= foe.defShredUntil) {
      const shred = Math.round(foe.def * pct);
      foe.def = Math.max(0, foe.def - shred);
      foe.defShredRestore = shred;
      eng.spawnFloatAtUnit(foe, `ARMOUR -${Math.round(pct * 100)}%`, FLOAT_COLORS.info);
    }
    foe.defShredUntil = now + (sk.shred_ms || 4000);
  };

  const castSkill = (sk) => {
    const c = combatRef.current;
    if (!c || scene !== "combat") return;
    const caster = c.heroes.find((h) => h.id === sk.owner && h.alive);
    if (!caster) return;
    const eng = engineRef.current;
    const now = performance.now();

    // ── Tier-4 ULTIMATES — energy-gated (100), not cooldown-gated ────────────
    if (sk.kind === "ultimate") {
      if (caster.energy < caster.maxEnergy) { flashToast("Ultimate charging — needs full Energy"); return; }
      caster.energy = 0;
      sfx.crit();
      eng.screenFlash("#00e5ff"); // localized full-canvas Ultimate strobe
      if (sk.ult === "fortress") {
        // Fortress Overdrive — 400 squad shield + stun all enemies.
        for (const ally of aliveOf(c.heroes)) { ally.shield += (sk.shield_flat || 400); eng.spawnAura(ally, "#ff00ff"); }
        for (const foe of aliveOf(c.enemies)) { foe.stunnedUntil = now + (sk.stun_ms || 2500); eng.spawnFloatAtUnit(foe, "STUN", FLOAT_COLORS.crit); }
        eng.spawnFloatAtUnit(caster, "FORTRESS", FLOAT_COLORS.info);
      } else if (sk.ult === "singularity") {
        // Singularity Strike — 350% AoE; crits pierce 50% of armour.
        for (const foe of aliveOf(c.enemies)) {
          const crit = Math.random() < caster.crit;
          const effDef = crit ? Math.round(foe.def * (1 - (sk.def_pierce || 0))) : foe.def;
          let base = Math.max(1, caster.atk * (sk.mult || 3.5) - effDef);
          if (crit) base *= 1.5;
          let raw = Math.round(base), dmg = raw;
          if (foe.shield > 0) { const ab = Math.min(foe.shield, dmg); foe.shield -= ab; dmg -= ab; }
          foe.hp = Math.max(0, foe.hp - dmg); if (foe.hp === 0) foe.alive = false;
          eng.spawnProjectile(caster, foe, "#ff00ff", crit);
          eng.spawnFloatAtUnit(foe, `-${raw}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit);
        }
        if (aliveOf(c.enemies).length === 0) { rerender(); return endCombat(true); }
      } else if (sk.ult === "aegis_cleave") {
        // Valkyrie · Aegis Cleave — orange slash wave over all enemies; AoE hit +
        // strips 30% of each target's defense (armour shred) for 6s.
        eng.slashWave("#ff9a3c");
        for (const foe of aliveOf(c.enemies)) {
          const { dmg, crit } = attackUnit(caster, foe, sk.mult || 2.6);
          eng.spawnFloatAtUnit(foe, `-${dmg}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit);
          applyArmourShred(foe, { def_shred: sk.def_shred || 0.30, shred_ms: sk.shred_ms || 6000 }, now, eng);
        }
        eng.spawnFloatAtUnit(caster, "AEGIS CLEAVE", FLOAT_COLORS.info);
        if (aliveOf(c.enemies).length === 0) { rerender(); return endCombat(true); }
      } else if (sk.ult === "umbral_strike") {
        // Shadow · Umbral Strike — multi-strike on the HIGHEST-HP target + 2s paralysis.
        const foe = aliveOf(c.enemies).sort((a, b) => b.hp - a.hp)[0];
        if (foe) {
          const hits = sk.hits || 4; let total = 0;
          for (let i = 0; i < hits; i++) {
            const { dmg, crit } = attackUnit(caster, foe, sk.mult || 1.15);
            total += dmg; eng.spawnProjectile(caster, foe, "#b061ff", crit);
          }
          eng.spawnFloatAtUnit(foe, `-${total}`, FLOAT_COLORS.crit, true);
          foe.stunnedUntil = now + (sk.stun_ms || 2000);
          eng.spawnFloatAtUnit(foe, "PARALYSED", FLOAT_COLORS.crit);
        }
        eng.spawnFloatAtUnit(caster, "UMBRAL STRIKE", FLOAT_COLORS.info);
        if (aliveOf(c.enemies).length === 0) { rerender(); return endCombat(true); }
      } else if (sk.ult === "overdrive_cascade") {
        // Roxy · Overdrive Cascade — magenta field over the hero column; +50% auto
        // cadence and life-steal on ALL deployed-unit attacks for 5s.
        eng.columnField("#ff36c8");
        c.overdriveUntil = now + (sk.buff_ms || 5000);
        c.overdriveLifesteal = sk.lifesteal || 0.30;
        for (const ally of aliveOf(c.heroes)) eng.spawnFloatAtUnit(ally, "OVERDRIVE", FLOAT_COLORS.info);
      }
      rerender();
      return;
    }

    if (!cdRef.current.ready(sk.id)) return;
    cdRef.current.trigger(sk.id, sk.cd || 1500);
    sfx.cast();

    if (sk.kind === "heal") {
      const allies = sk.aoe ? aliveOf(c.heroes) : [lowestHp(c.heroes)];
      for (const ally of allies) {
        if (!ally) continue;
        const { healed } = healUnit(ally, Math.round((sk.heal_pct || 0.25) * ally.maxHp));
        eng.spawnFloatAtUnit(ally, `+${healed}`, FLOAT_COLORS.heal);
        eng.spawnHealGlow(ally);
        if (sk.def_buff_pct) ally.def += Math.round(ally.baseDef * sk.def_buff_pct);
      }
      sfx.heal();
    } else if (sk.kind === "shield_self") {
      caster.shield += Math.round((sk.shield_pct || 0.3) * caster.maxHp);
      eng.spawnAura(caster, "#00d5ff");
      eng.spawnFloatAtUnit(caster, "SHIELD", FLOAT_COLORS.info);
    } else if (sk.kind === "buff") {
      caster.def += Math.round(caster.baseDef * (sk.def_buff_pct || 0.3));
      if (sk.taunt) caster.taunt = true;
      eng.spawnAura(caster, sk.taunt ? "#f59e0b" : "#00d5ff");
      eng.spawnFloatAtUnit(caster, sk.taunt ? "TAUNT" : "BUFF", FLOAT_COLORS.info);
    } else if (sk.kind === "stun") {
      const foes = sk.aoe ? aliveOf(c.enemies) : [lowestHp(c.enemies)];
      if (sk.aoe) eng.spawnAura(caster, "#f5c542");
      let anyCrit = false;
      for (const foe of foes) {
        if (!foe) continue;
        eng.spawnProjectile(caster, foe, caster.color, !!sk.mult);
        if (sk.mult) {
          const { dmg, crit } = attackUnit(caster, foe, sk.mult);
          if (crit) anyCrit = true;
          eng.spawnFloatAtUnit(foe, `-${dmg}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit);
        }
        foe.stunnedUntil = now + (sk.stun_ms || 2000);
        if (!sk.mult) eng.spawnFloatAtUnit(foe, "STUN", FLOAT_COLORS.crit);
      }
      if (sk.mult) {
        anyCrit ? sfx.crit() : sfx.hit();
        if (aliveOf(c.enemies).length === 0) { rerender(); return endCombat(true); }
      }
    } else if (sk.kind === "stun_shield") {
      const foe = lowestHp(c.enemies);
      if (foe) {
        eng.spawnProjectile(caster, foe, caster.color, true);
        if (sk.mult) { const { dmg, crit } = attackUnit(caster, foe, sk.mult); eng.spawnFloatAtUnit(foe, `-${dmg}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit); }
        foe.stunnedUntil = now + (sk.stun_ms || 2500);
        eng.spawnFloatAtUnit(foe, "STUN", FLOAT_COLORS.crit);
      }
      caster.shield += Math.round((sk.shield_pct || 0.15) * caster.maxHp);
      eng.spawnAura(caster, "#00d5ff");
      if (aliveOf(c.enemies).length === 0) { rerender(); return endCombat(true); }
    } else { // attack
      const foes = sk.aoe ? aliveOf(c.enemies) : [lowestHp(c.enemies)];
      const odActive = now < (c.overdriveUntil || 0);
      let anyCrit = false, stolen = 0;
      for (const foe of foes) {
        if (!foe) continue;
        const { dmg, crit } = attackUnit(caster, foe, sk.mult || 1);
        if (crit) anyCrit = true;
        if (odActive && dmg > 0) stolen += Math.round(dmg * (c.overdriveLifesteal || 0.30));
        eng.spawnProjectile(caster, foe, crit ? FLOAT_COLORS.crit : (caster.color || "#ffd479"), crit);
        eng.spawnFloatAtUnit(foe, `-${dmg}`, crit ? FLOAT_COLORS.crit : FLOAT_COLORS.damage, crit);
        if (sk.def_shred) applyArmourShred(foe, sk, now, eng);
      }
      if (stolen > 0) { healUnit(caster, stolen); eng.spawnFloatAtUnit(caster, `+${stolen}`, FLOAT_COLORS.heal); }
      anyCrit ? sfx.crit() : sfx.hit();
      if (aliveOf(c.enemies).length === 0) { rerender(); return endCombat(true); }
    }
    // Energy generation (Corridor Energy Surge tier): basic skill casts award +50.
    caster.energy = Math.min(caster.maxEnergy, caster.energy + SKILL_ENERGY);
    rerender();
  };

  const endCombat = (win) => {
    stopEnemyLoop();
    win ? sfx.victory() : sfx.defeat();
    if (win) {
      setScene("victory");
      const idx = square;
      window.setTimeout(() => {
        stateRef.current = saveState(clearTile(stateRef.current, idx));
        if (idx === 2) flashToast("Nancy recruited — Biotech Heal online!");
        syncUp();
        if (corridorComplete(stateRef.current)) {
          setScene("gate");
          window.setTimeout(() => onComplete?.(), 2600);
        } else {
          pushTrek();
          setScene("trek");
        }
      }, 1500);
    } else {
      setScene("defeat");
    }
    rerender();
  };

  const retry = () => startCombat(square);

  // keep a live ref of scene for the interval closure
  const sceneRef = useRef(scene);
  useEffect(() => { sceneRef.current = scene; }, [scene]);

  // ── Engine bootstrap ──────────────────────────────────────────────────────
  useEffect(() => {
    const engine = new SpaceEngine(canvasRef.current, { onTileArrive });
    engineRef.current = engine;
    engine.mount();
    pushTrek();
    applyAssets();
    sfx.startBgm(BGM_URL);
    const onResize = () => engine.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); stopEnemyLoop(); engine.unmount(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const st = stateRef.current;
  const teamSkills = squad.flatMap((h) => (h.skills || []).map((s) => ({ ...s, owner: h.card_id, ownerName: h.name })));
  const c = combatRef.current;

  return (
    <div className="ss-viewport" data-testid="space-supremacy-app">
      <canvas ref={canvasRef} className="ss-canvas" data-testid="space-trek-canvas" />

      <button className="ss-home-btn" data-testid="space-home-btn" onClick={onHome} aria-label="Back to Games Hub">
        <Home size={18} />
      </button>
      <SpaceMuteBtn testid="space-mute-btn" />

      {/* Trek objective banner */}
      {scene === "trek" && (
        <div className="ss-trek-banner" data-testid="space-trek-banner">
          <span className="eb">Stage 1 · Migration Corridor</span>
          <span className="ob">{CORRIDOR_TILES[st.corridor.findIndex((v) => v === TileState.UNCLEARED)]?.name || "Sanctuary reached"}</span>
        </div>
      )}

      {toast && <div className="ss-toast" data-testid="space-toast">{toast}</div>}

      {/* Combat HUD */}
      {scene === "combat" && c && (
        <>
          <div className="ss-team-hud" data-testid="space-team-hud">
            {c.heroes.map((h) => (
              <div key={h.uid} className={`ss-portrait ${h.alive ? "" : "ko"}`} data-testid={`space-portrait-${h.id}`}>
                <span className="pnm" style={{ color: h.color }}>{h.name}</span>
                <div className="phpbar"><span style={{ width: `${(h.hp / h.maxHp) * 100}%` }} /></div>
                <span className="php">{h.hp}/{h.maxHp}</span>
                <div className="penbar" data-testid={`space-energy-${h.id}`}>
                  <span className={h.energy >= h.maxEnergy ? "full" : ""} style={{ width: `${(h.energy / h.maxEnergy) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="ss-skillbar" data-testid="space-skillbar">
            {teamSkills.map((s) => {
              const owner = c.heroes.find((h) => h.id === s.owner);
              const isUlt = s.kind === "ultimate";
              const charged = owner ? owner.energy >= owner.maxEnergy : false;
              const frac = isUlt ? 0 : cdRef.current.fraction(s.id, s.cd);
              const disabled = !owner?.alive || (isUlt && !charged);
              return (
                <button key={s.id} className={`ss-skill ${s.kind} ${isUlt && charged ? "charged" : ""}`}
                  data-testid={`space-skill-${s.id.replace(":", "-")}`}
                  disabled={disabled} onClick={() => castSkill(s)}>
                  <span className="sk-lbl">{s.name}</span>
                  <span className="sk-own">{s.ownerName}{isUlt ? " · ULT" : ""}</span>
                  {frac > 0 && <span className="sk-cd" style={{ height: `${frac * 100}%` }} />}
                  {isUlt && !charged && owner && <span className="sk-en" data-testid={`space-ult-charge-${s.owner}`}>{Math.floor((owner.energy / owner.maxEnergy) * 100)}%</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Victory / Defeat / Gate overlays */}
      {scene === "victory" && (
        <div className="ss-overlay win" data-testid="space-victory">
          <span className="ov-t">Threat Neutralized</span>
          <span className="ov-s">{CORRIDOR_TILES[square]?.name} cleared</span>
        </div>
      )}
      {scene === "defeat" && (
        <div className="ss-overlay lose" data-testid="space-defeat">
          <span className="ov-t">Squad Down</span>
          <button className="ss-enter-btn" data-testid="space-retry" onClick={retry}>Retry Engagement</button>
        </div>
      )}
      {scene === "gate" && (
        <div className="ss-overlay gate" data-testid="space-gate">
          <span className="ov-t">Gatehouse Alpha Breached</span>
          <span className="ov-s">Access codes accepted · sealing the sanctuary…</span>
        </div>
      )}
    </div>
  );
}
